import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronDown, Stethoscope } from "lucide-react";
import { SYMPTOMS, BIBLIOGRAPHY, scoreSubstances, isEmergency, EMERGENCY_TEXT, SHORT_WINDOW_NOTE } from "./spikingData";

const testingHref = (ids) => `/safety/testing${ids?.length ? `?substances=${ids.join(",")}` : ""}`;

const TestingButton = ({ ids, label, big = false, testId }) => (
  <Link
    to={testingHref(ids)}
    className={`inline-flex items-center justify-center gap-2 rounded-full bg-[#1D4ED8] text-white font-extrabold hover:bg-[#1e40af] transition-colors no-underline ${big ? "w-full min-h-[56px] px-6 py-3.5 text-base md:text-lg" : "min-h-[40px] px-3.5 py-1.5 text-xs"}`}
    data-testid={testId}
  >
    <Stethoscope className={big ? "h-5 w-5" : "h-3.5 w-3.5"} /> {label}
  </Link>
);

const StrengthPill = ({ strength }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wide ${strength.key === "strong" ? "bg-[#1D4ED8] text-white" : "bg-[#E2E0DC] text-[#3a3a3a]"}`}
    data-testid={`substance-strength-${strength.key}`}
  >
    {strength.label}
  </span>
);

const SubstanceCard = ({ m, i, onSourceClick }) => (
  <div className={`rounded-lg border-l-4 bg-[#F7F9FF] px-4 py-3 ${m.strength.key === "strong" ? "border-[#1D4ED8]" : "border-[#B9C3DD]"}`} data-testid={`substance-card-${m.id}`}>
    <div className="flex flex-wrap items-center gap-2">
      <StrengthPill strength={m.strength} />
      <span className="font-bold text-[#1A1A1A] text-base">{m.name}</span>
    </div>
    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <span className="font-bold uppercase text-[#1A1A1A] text-sm" data-testid={`substance-within-${i}`}>Test within {m.within}</span>
      <span className="text-xs font-semibold text-[#1D4ED8]">· window: {m.window}</span>
      <TestingButton ids={[m.id]} label="Testing" testId={`substance-testing-${m.id}`} />
    </div>
    {m.short_window && <div className="mt-1 text-sm text-[#3a3a3a]" data-testid={`substance-past-window-${m.id}`}>{SHORT_WINDOW_NOTE}</div>}
    <div className="mt-1.5 text-sm text-[#1A1A1A] font-bold" data-testid={`substance-test-${m.id}`}>Test: {m.test}</div>
    <div className="mt-0.5 text-sm text-[#5C5C5C]">{m.note}</div>
    <div className="mt-1.5 text-xs text-[#5C5C5C]" data-testid={`substance-refs-${i}`}>Sources:{" "}
      {m.refs.map((n) => (
        <a key={n} href={`#north-star-src-${n}`} onClick={(e) => { e.preventDefault(); onSourceClick?.(n); }} title={BIBLIOGRAPHY[n - 1]?.label} className="underline text-[#1D4ED8] font-semibold mr-1.5" data-testid={`substance-ref-${i}-${n}`}>[{n}]</a>
      ))}
      <span>· see Sources &amp; bibliography below</span>
    </div>
  </div>
);

// Tap your symptoms → weighted match (src/data/spikingScoring.json) → what to ask to be tested for, and where.
export const SymptomChecker = ({ onSourceClick }) => {
  const [picked, setPicked] = useState([]);
  const [more, setMore] = useState(false);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const matches = scoreSubstances(picked);
  const top = matches.slice(0, 4);
  const rest = matches.slice(4);
  const topIds = matches.slice(0, 3).map((m) => m.id);
  return (
    <div className="rounded-xl border border-[#1D4ED8]/20 bg-white p-5" data-testid="symptom-checker">
      <h3 className="text-xl md:text-2xl font-extrabold text-[#1D4ED8]" data-testid="symptom-checker-heading">What should I focus my testing on?</h3>
      <p className="mt-2 text-base md:text-lg leading-relaxed text-[#3a3a3a]" data-testid="symptom-checker-intro">Tap what you're feeling. This tells the clinician what to test for. It does not diagnose. <Link to="/disclaimer" className="underline text-[#1D4ED8]" data-testid="symptom-disclaimer-link">Read the full disclaimer</Link>.</p>
      <div className="mt-4 grid sm:grid-cols-2 gap-2">
        {SYMPTOMS.map((s) => {
          const on = picked.includes(s.id);
          return (
            <label key={s.id} className={`flex items-start gap-2.5 rounded-lg border-2 px-3 py-3 min-h-[56px] text-base cursor-pointer transition-colors ${on ? "border-[#1D4ED8] bg-[#1D4ED8] text-white shadow-md" : "border-[#E2E0DC] text-[#1A1A1A] hover:bg-[#F7F9FF]"}`} data-testid={`symptom-tile-${s.id}`} aria-pressed={on}>
              <input type="checkbox" checked={on} onChange={() => toggle(s.id)} className="sr-only" data-testid={`symptom-${s.id}`} />
              <span className={`mt-0.5 h-5 w-5 shrink-0 rounded border-2 inline-flex items-center justify-center ${on ? "border-white bg-white text-[#1D4ED8]" : "border-[#9A9A9A]"}`} aria-hidden>{on && <Check className="h-3.5 w-3.5" strokeWidth={4} />}</span>
              <span className="font-semibold">{s.label}</span>
            </label>
          );
        })}
      </div>

      {picked.length === 0 && (
        <div className="mt-5" data-testid="symptom-testing-idle">
          <TestingButton ids={[]} label="Testing — who to call, what to say" big testId="symptom-testing-btn" />
        </div>
      )}

      {picked.length > 0 && (
        <div className="mt-5 space-y-3" data-testid="symptom-results">
          {isEmergency(picked) && (
            <div className="rounded-xl border-l-4 border-[#B91C1C] bg-[#FEF2F2] px-5 py-4" data-testid="symptom-911-card">
              <a href="tel:911" className="block text-lg md:text-xl font-extrabold text-[#B91C1C] no-underline">{EMERGENCY_TEXT}</a>
            </div>
          )}
          <TestingButton ids={topIds} label="Get tested for these — where to go" big testId="symptom-testing-btn" />
          <p className="text-xs text-[#5C5C5C] -mt-1" data-testid="symptom-meds-note">Tell staff every medication you take.</p>
          <div className="text-sm font-extrabold uppercase tracking-wide text-[#1A1A1A]">Possibly associated — ask to be tested for:</div>
          {top.map((m, i) => <SubstanceCard key={m.id} m={m} i={i} onSourceClick={onSourceClick} />)}
          {rest.length > 0 && (
            <div>
              <button type="button" onClick={() => setMore(!more)} aria-expanded={more} className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#1D4ED8]/30 bg-white px-4 py-2 text-sm font-extrabold text-[#1D4ED8] hover:border-[#1D4ED8]" data-testid="symptom-show-more">
                {more ? "Hide" : "Show"} more possibilities ({rest.length}) <ChevronDown className={`h-4 w-4 transition-transform ${more ? "rotate-180" : ""}`} />
              </button>
              {more && <div className="mt-3 space-y-3" data-testid="symptom-more-results">{rest.map((m, i) => <SubstanceCard key={m.id} m={m} i={i + 4} onSourceClick={onSourceClick} />)}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SymptomChecker;
